import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Search, Mail, X, Plus, UserPlus, Edit, Trash, Car, Briefcase, AlertCircle, AtSign, 
  Calendar, CheckCircle, XCircle, User, Bike, Trophy, DollarSign, RefreshCw, Download, 
  Filter, PenTool as Tool, RotateCcw, Clock, Truck, Settings, FileText, Database, KeyRound
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';

interface User {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  company_name?: string;
  vat_number?: string;
  billing_address?: string;
  is_admin: boolean;
  role: 'user' | 'worker' | 'admin' | 'driver' | 'promotor';
  created_at: string;
  temporary_password?: boolean;
  password_set_by_admin?: boolean;
  password_set_date?: string;
  password_expires_at?: string;
}

interface EventRegistration {
  id: string;
  event_id: string;
  category_id: string;
  user_id: string;
  driver_profile_id: string;
  amount_paid: number;
  status: string;
  payment_status: string;
  has_tool_car: boolean;
  tool_car_count: number;
  created_at: string;
  event: {
    name: string;
    event_date: string;
  };
  category: {
    name: string;
    vehicle_type: string;
    registration_fee: number;
  };
  driver_profile?: {
    vehicle_type: string;
    vehicle_info: any;
    personal_info: any;
    driver_info: any;
  };
}

interface DriverProfile {
  id: string;
  user_id: string;
  vehicle_type: 'auto' | 'motorcycle';
  personal_info: any;
  vehicle_info: any;
  driver_info: any;
  is_complete: boolean;
  created_at: string;
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  event_categories: EventCategory[];
}

interface EventCategory {
  id: string;
  name: string;
  vehicle_type: string;
  registration_fee: number;
}

export default function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function to format date for display (avoiding timezone issues)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Main state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'clients' | 'workers'>('clients');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showDriverProfileModal, setShowDriverProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserRegistrations, setSelectedUserRegistrations] = useState<EventRegistration[]>([]);
  const [selectedUserDriverProfiles, setSelectedUserDriverProfiles] = useState<DriverProfile[]>([]);
  const [availableEvents, setAvailableEvents] = useState<Event[]>([]);
  const [availableCategories, setAvailableCategories] = useState<EventCategory[]>([]);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferRegistration, setTransferRegistration] = useState<EventRegistration | null>(null);
  const [transferTargetUser, setTransferTargetUser] = useState<string>('');
  const [activeUserTab, setActiveUserTab] = useState<'profile' | 'registrations' | 'driver-profiles'>('profile');
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPasswordForm, setTempPasswordForm] = useState({
    userId: '',
    userName: '',
    temporaryPassword: '',
    expiresInDays: 7
  });

  // Form states
  const [workerForm, setWorkerForm] = useState({
    email: '',
    full_name: '',
    phone: '',
    password: ''
  });

  const [userEditForm, setUserEditForm] = useState({
    id: '',
    email: '',
    full_name: '',
    phone: '',
    role: 'user' as 'user' | 'driver' | 'promotor'
  });

  const [driverProfileForm, setDriverProfileForm] = useState({
    vehicle_type: 'auto' as 'auto' | 'motorcycle',
    personal_info: {
      fullName: '',
      address: '',
      idNumber: '',
      dateOfBirth: '',
      nationality: '',
      country: '',
      mobilePhone: '',
      email: '',
      whatsapp: '',
      representativeOfOwner: ''
    },
    vehicle_info: {
      vehicleNumber: '',
      raceNumber: '',
      vehicleBrandModel: '',
      color: '',
      engineBrand: '',
      year: '',
      numberOfCylinders: ''
    },
    driver_info: {
      driverName: '',
      licenseNumber: '',
      expirationDate: '',
      teamName: '',
      clubName: '',
      nickname: '',
      address: '',
      dateOfBirth: '',
      nationality: '',
      country: '',
      mobilePhone: '',
      email: '',
      idNumber: ''
    }
  });

  const [registrationForm, setRegistrationForm] = useState({
    event_id: '',
    category_id: '',
    amount_paid: 0,
    has_tool_car: false,
    tool_car_count: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchUsers();
    fetchAvailableEvents();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          name,
          event_date,
          event_categories (
            id,
            name,
            vehicle_type,
            registration_fee
          )
        `)
        .order('event_date', { ascending: false });

      if (error) throw error;
      setAvailableEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleUserDetails = async (userToView: User) => {
    setSelectedUser(userToView);
    setActiveUserTab('profile'); // Reset to profile tab when opening user details
    setUserEditForm({
      id: userToView.id,
      email: userToView.email,
      full_name: userToView.full_name,
      phone: userToView.phone,
      role: userToView.role as 'user' | 'driver' | 'promotor'
    });

    // Fetch user's registrations
    try {
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name, vehicle_type, registration_fee),
          driver_profile:driver_profiles(vehicle_type, vehicle_info, personal_info, driver_info)
        `)
        .eq('user_id', userToView.id)
        .order('created_at', { ascending: false });

      if (regError) throw regError;
      setSelectedUserRegistrations(registrations || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load user registrations');
    }

    // Fetch user's driver profiles
    try {
      const { data: profiles, error: profileError } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', userToView.id);

      if (profileError) throw profileError;
      setSelectedUserDriverProfiles(profiles || []);
    } catch (error) {
      console.error('Error fetching driver profiles:', error);
      toast.error('Failed to load driver profiles');
    }

    setShowUserDetailsModal(true);
  };

  const handleCreateWorker = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: workerForm.email,
        password: workerForm.password,
        options: {
          data: {
            full_name: workerForm.full_name,
            phone: workerForm.phone,
            role: 'worker'
          }
        }
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Failed to create auth user');
      }

      const { error: userError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: workerForm.email,
          full_name: workerForm.full_name,
          phone: workerForm.phone,
          role: 'worker',
          is_admin: false
        }]);

      if (userError) throw userError;

      toast.success('Worker account created successfully');
      setShowWorkerModal(false);
      setWorkerForm({ email: '', full_name: '', phone: '', password: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating worker:', error);
      toast.error('Failed to create worker account');
    }
  };

  const handleUserUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Update user role
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-user-role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userEditForm.id,
          role: userEditForm.role
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to update user role');
      }

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({
          full_name: userEditForm.full_name,
          phone: userEditForm.phone
        })
        .eq('id', userEditForm.id);

      if (updateError) throw updateError;

      toast.success('User updated successfully');
      
      // Fetch fresh user data from database
      const { data: updatedUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userEditForm.id)
        .single();

      if (fetchError) throw fetchError;

      // Update the selected user with fresh data
      if (updatedUser) {
        setSelectedUser(updatedUser);
        // Also update the form with the fresh data
        setUserEditForm({
          id: updatedUser.id,
          email: updatedUser.email,
          full_name: updatedUser.full_name,
          phone: updatedUser.phone,
          role: updatedUser.role as 'user' | 'driver' | 'promotor'
        });
      }

      // Refresh the users list
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleEmailUpdate = async (newEmail: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-user-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userEditForm.id,
          newEmail: newEmail
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to update user email');
      }

      // Fetch fresh user data from database
      const { data: updatedUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userEditForm.id)
        .single();

      if (fetchError) throw fetchError;

      // Update the selected user with fresh data
      if (updatedUser) {
        setSelectedUser(updatedUser);
        // Also update the form with the fresh data
        setUserEditForm({
          id: updatedUser.id,
          email: updatedUser.email,
          full_name: updatedUser.full_name,
          phone: updatedUser.phone,
          role: updatedUser.role as 'user' | 'driver' | 'promotor'
        });
      }

      toast.success('Email updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error('Failed to update email');
    }
  };

  // Generate a secure temporary password
  const generateTemporaryPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Handle setting temporary password
  const handleSetTemporaryPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tempPasswordForm.temporaryPassword || tempPasswordForm.temporaryPassword.length < 6) {
      toast.error('Temporary password must be at least 6 characters long');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/set-temporary-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: tempPasswordForm.userId,
          temporaryPassword: tempPasswordForm.temporaryPassword
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to set temporary password');
      }

      // Update the user's password expiration date
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + tempPasswordForm.expiresInDays);

      const { error: updateError } = await supabase
        .from('users')
        .update({
          password_expires_at: expirationDate.toISOString()
        })
        .eq('id', tempPasswordForm.userId);

      if (updateError) throw updateError;

      toast.success(`Temporary password set successfully for ${tempPasswordForm.userName}`);
      setShowTempPasswordModal(false);
      setTempPasswordForm({
        userId: '',
        userName: '',
        temporaryPassword: '',
        expiresInDays: 7
      });
      
      // Refresh users list
      fetchUsers();
    } catch (error) {
      console.error('Error setting temporary password:', error);
      toast.error('Failed to set temporary password');
    }
  };

  // Open temporary password modal
  const openTempPasswordModal = (user: User) => {
    setTempPasswordForm({
      userId: user.id,
      userName: user.full_name,
      temporaryPassword: generateTemporaryPassword(),
      expiresInDays: 7
    });
    setShowTempPasswordModal(true);
  };

  const handleDriverProfileUpdate = async (profileId: string) => {
    try {
      console.log('Updating driver profile:', profileId);
      
      // Use the same approach as the regular driver profile system
      const profileData = {
        personal_info: driverProfileForm.personal_info,
        vehicle_info: driverProfileForm.vehicle_info,
        driver_info: driverProfileForm.driver_info,
        is_complete: true,
        updated_at: new Date().toISOString()
      };

      console.log('Update data:', profileData);

      // Update the profile (same as useDriverProfile.ts)
      const { error } = await supabase
        .from('driver_profiles')
        .update(profileData)
        .eq('id', profileId);

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update completed successfully');

      toast.success('Driver profile updated successfully');
      
      // Refresh driver profiles
      const { data: profiles, error: profileError } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', selectedUser?.id);

      if (profileError) {
        console.error('Error refreshing profiles:', profileError);
        throw profileError;
      }
      
      console.log('Refreshed profiles:', profiles);
      setSelectedUserDriverProfiles(profiles || []);
      
      // Close the modal
      setShowDriverProfileModal(false);
      setEditingProfileId(null);
    } catch (error) {
      console.error('Error updating driver profile:', error);
      toast.error('Failed to update driver profile');
    }
  };

  const handleRemoveRegistration = async (registrationId: string) => {
    if (!confirm('Are you sure you want to remove this registration?')) return;

    try {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('id', registrationId);

      if (error) throw error;

      toast.success('Registration removed successfully');
      
      // Refresh registrations
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name, vehicle_type, registration_fee),
          driver_profile:driver_profiles(vehicle_type, vehicle_info, personal_info, driver_info)
        `)
        .eq('user_id', selectedUser?.id)
        .order('created_at', { ascending: false });

      if (regError) throw regError;
      setSelectedUserRegistrations(registrations || []);
    } catch (error) {
      console.error('Error removing registration:', error);
      toast.error('Failed to remove registration');
    }
  };

  const handleTransferRegistration = async (registration: EventRegistration) => {
    setTransferRegistration(registration);
    setTransferTargetUser('');
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferRegistration || !transferTargetUser) return;

    try {
      // Check if target user has appropriate driver profile
      const selectedCategory = availableCategories.find(cat => cat.id === transferRegistration.category_id);
      if (!selectedCategory) throw new Error('Category not found');

      const vehicleTypeMap = { car: 'auto', bike: 'motorcycle' };
      const requiredVehicleType = vehicleTypeMap[selectedCategory.vehicle_type as keyof typeof vehicleTypeMap];
      
      const { data: targetProfiles, error: profileError } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', transferTargetUser)
        .eq('vehicle_type', requiredVehicleType)
        .eq('is_complete', true);

      if (profileError) throw profileError;

      if (!targetProfiles || targetProfiles.length === 0) {
        toast.error(`Target user needs a complete ${selectedCategory.vehicle_type} driver profile to receive this registration`);
        return;
      }

      // Update registration
      const { error } = await supabase
        .from('event_registrations')
        .update({
          user_id: transferTargetUser,
          driver_profile_id: targetProfiles[0].id
        })
        .eq('id', transferRegistration.id);

      if (error) throw error;

      toast.success('Registration transferred successfully');
      setShowTransferModal(false);
      
      // Refresh registrations
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name, vehicle_type, registration_fee),
          driver_profile:driver_profiles(vehicle_type, vehicle_info, personal_info, driver_info)
        `)
        .eq('user_id', selectedUser?.id)
        .order('created_at', { ascending: false });

      if (regError) throw regError;
      setSelectedUserRegistrations(registrations || []);
    } catch (error) {
      console.error('Error transferring registration:', error);
      toast.error('Failed to transfer registration');
    }
  };

  const handleAddRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      // Check if user is already registered for this event + category
      const { data: existingReg, error: checkError } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', registrationForm.event_id)
        .eq('category_id', registrationForm.category_id)
        .eq('user_id', selectedUser.id)
        .is('original_registration_id', null) // Only check original registrations
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingReg) {
        toast.error('User is already registered for this event and category');
        return;
      }

      // Find matching driver profile
      const selectedCategory = availableCategories.find(cat => cat.id === registrationForm.category_id);
      if (!selectedCategory) throw new Error('Category not found');

      const vehicleTypeMap = { car: 'auto', bike: 'motorcycle' };
      const requiredVehicleType = vehicleTypeMap[selectedCategory.vehicle_type as keyof typeof vehicleTypeMap];
      
      const driverProfile = selectedUserDriverProfiles.find(
        profile => profile.vehicle_type === requiredVehicleType && profile.is_complete
      );

      if (!driverProfile) {
        toast.error(`User needs a complete ${selectedCategory.vehicle_type} driver profile to register for this category`);
        return;
      }

      const { error } = await supabase
        .from('event_registrations')
        .insert([{
          event_id: registrationForm.event_id,
          category_id: registrationForm.category_id,
          user_id: selectedUser.id,
          driver_profile_id: driverProfile.id,
          amount_paid: registrationForm.amount_paid,
          has_tool_car: registrationForm.has_tool_car,
          tool_car_count: registrationForm.tool_car_count
        }]);

      if (error) throw error;

      toast.success('Registration added successfully');
      
      // Reset form
      setRegistrationForm({
        event_id: '',
        category_id: '',
        amount_paid: 0,
        has_tool_car: false,
        tool_car_count: 0
      });

      // Refresh registrations
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name, vehicle_type, registration_fee),
          driver_profile:driver_profiles(vehicle_type, vehicle_info, personal_info, driver_info)
        `)
        .eq('user_id', selectedUser.id)
        .order('created_at', { ascending: false });

      if (regError) throw regError;
      setSelectedUserRegistrations(registrations || []);
    } catch (error) {
      console.error('Error adding registration:', error);
      toast.error('Failed to add registration');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This will also delete all their registrations and driver profiles.')) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      toast.success('User deleted successfully');
      fetchUsers();
      if (selectedUser?.id === userId) {
        setShowUserDetailsModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleEventChange = (eventId: string) => {
    setRegistrationForm({ ...registrationForm, event_id: eventId, category_id: '' });
    const event = availableEvents.find(e => e.id === eventId);
    setAvailableCategories(event?.event_categories || []);
  };

  // Reset to first page when search or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'driver': return <Car className="h-4 w-4 text-blue-400" />;
      case 'promotor': return <Briefcase className="h-4 w-4 text-amber-400" />;
      default: return <Users className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'user': return 'User';
      case 'worker': return 'Worker';
      case 'admin': return 'Admin';
      case 'driver': return 'Driver';
      case 'promotor': return 'Promotor';
      default: return role;
    }
  };

  // Filter users based on search term and active tab
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'workers' ? user.role === 'worker' : user.role !== 'worker';
    
    return matchesSearch && matchesTab;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          {activeTab === 'workers' && (
            <button
              onClick={() => {
                setWorkerForm({ email: '', full_name: '', phone: '', password: '' });
                setShowWorkerModal(true);
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <UserPlus className="h-5 w-5" />
              Add Worker
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'clients'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Clients
          </button>
          <button
            onClick={() => setActiveTab('workers')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'workers'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Workers
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-900 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        {/* Users Table */}
        <div className="bg-zinc-900 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-800">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Member Since
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {currentUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {user.full_name}
                        <div className="flex items-center gap-1 mt-1">
                          {getRoleIcon(user.role)}
                          <span className="text-xs text-gray-400">{getRoleDisplayName(user.role)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{user.email}</div>
                      <div className="text-sm text-gray-400">{user.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {user.is_admin ? 'Admin' : 'Active'}
                        </span>
                        {user.temporary_password && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Temp Password
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUserDetails(user)}
                          className="text-gray-400 hover:text-blue-500"
                          title="View Details"
                        >
                          <Settings className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openTempPasswordModal(user)}
                          className="text-gray-400 hover:text-yellow-500"
                          title="Set Temporary Password"
                        >
                          <KeyRound className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-gray-400 hover:text-red-500"
                          title="Delete User"
                        >
                          <Trash className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredUsers.length}
          />
        )}

        {/* Users Count */}
        <div className="mb-4 text-sm text-gray-400">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} {activeTab === 'workers' ? 'workers' : 'clients'}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">No {activeTab} found</p>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetailsModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-white">User Details - {selectedUser.full_name}</h2>
                <button onClick={() => setShowUserDetailsModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>

              {/* Tabs for different sections */}
              <div className="flex gap-4 mb-6 border-b border-zinc-700">
                <button 
                  onClick={() => setActiveUserTab('profile')}
                  className={`px-4 py-2 ${
                    activeUserTab === 'profile' 
                      ? 'text-white border-b-2 border-primary-500' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Profile
                </button>
                <button 
                  onClick={() => setActiveUserTab('registrations')}
                  className={`px-4 py-2 ${
                    activeUserTab === 'registrations' 
                      ? 'text-white border-b-2 border-primary-500' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Registrations
                </button>
                <button 
                  onClick={() => setActiveUserTab('driver-profiles')}
                  className={`px-4 py-2 ${
                    activeUserTab === 'driver-profiles' 
                      ? 'text-white border-b-2 border-primary-500' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Driver Profiles
                </button>
              </div>

              {/* Content based on active tab */}
              {activeUserTab === 'profile' && (
                <div className="space-y-6">
                <form onSubmit={handleUserUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                      <input
                        type="text"
                        value={userEditForm.full_name}
                        onChange={(e) => setUserEditForm({ ...userEditForm, full_name: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={userEditForm.email}
                          onChange={(e) => setUserEditForm({ ...userEditForm, email: e.target.value })}
                          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleEmailUpdate(userEditForm.email)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                        >
                          Update
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={userEditForm.phone}
                        onChange={(e) => setUserEditForm({ ...userEditForm, phone: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                      <select
                        value={userEditForm.role}
                        onChange={(e) => setUserEditForm({ ...userEditForm, role: e.target.value as any })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      >
                        <option value="user">User</option>
                        <option value="driver">Driver</option>
                        <option value="promotor">Promotor</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md"
                  >
                    Update Profile
                  </button>
                                 </form>
               </div>
               )}

               {activeUserTab === 'registrations' && (
                 <div className="space-y-6">
                   <div className="flex justify-between items-center mb-4">
                     <h3 className="text-xl font-semibold text-white">Event Registrations</h3>
                   </div>
                   
                   {/* Add Registration Form */}
                   <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                     <h4 className="text-lg font-medium text-white mb-4">Add New Registration</h4>
                     <form onSubmit={handleAddRegistration} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                       <div>
                         <label className="block text-sm font-medium text-gray-300 mb-1">Event</label>
                         <select
                           value={registrationForm.event_id}
                           onChange={(e) => handleEventChange(e.target.value)}
                           className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                           required
                         >
                           <option value="">Select Event</option>
                           {availableEvents.map((event) => (
                             <option key={event.id} value={event.id}>
                               {event.name} - {formatDateForDisplay(event.event_date)}
                             </option>
                           ))}
                         </select>
                       </div>
                       <div>
                         <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                         <select
                           value={registrationForm.category_id}
                           onChange={(e) => {
                             const category = availableCategories.find(cat => cat.id === e.target.value);
                             setRegistrationForm({
                               ...registrationForm,
                               category_id: e.target.value,
                               amount_paid: category?.registration_fee || 0
                             });
                           }}
                           className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                           required
                           disabled={!registrationForm.event_id}
                         >
                           <option value="">Select Category</option>
                           {availableCategories.map((category) => (
                             <option key={category.id} value={category.id}>
                               {category.name} - ${category.registration_fee}
                             </option>
                           ))}
                         </select>
                       </div>
                       <div>
                         <label className="block text-sm font-medium text-gray-300 mb-1">Amount Paid</label>
                         <input
                           type="number"
                           value={registrationForm.amount_paid}
                           onChange={(e) => setRegistrationForm({ ...registrationForm, amount_paid: parseFloat(e.target.value) || 0 })}
                           className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                           min="0"
                           step="0.01"
                         />
                       </div>
                       <div className="md:col-span-3">
                         <button
                           type="submit"
                           className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                           disabled={!registrationForm.event_id || !registrationForm.category_id}
                         >
                           Add Registration
                         </button>
                       </div>
                     </form>
                   </div>
                   
                   <div className="bg-zinc-800 rounded-lg overflow-hidden">
                     <table className="w-full">
                       <thead>
                         <tr className="bg-zinc-700">
                           <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Event</th>
                           <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Category</th>
                           <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Status</th>
                           <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Payment</th>
                           <th className="px-4 py-2 text-left text-xs font-medium text-gray-300">Actions</th>
                         </tr>
                       </thead>
                       <tbody className="divide-y divide-zinc-700">
                         {selectedUserRegistrations.map((reg) => (
                           <tr key={reg.id}>
                             <td className="px-4 py-2 text-sm text-white">
                               <div>{reg.event.name}</div>
                               <div className="text-xs text-gray-400">{formatDateForDisplay(reg.event.event_date)}</div>
                             </td>
                             <td className="px-4 py-2 text-sm text-white">{reg.category.name}</td>
                             <td className="px-4 py-2">
                               <span className={`px-2 py-1 text-xs rounded-full ${
                                 reg.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                 reg.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                 'bg-red-100 text-red-800'
                               }`}>
                                 {reg.status}
                               </span>
                             </td>
                             <td className="px-4 py-2">
                               <span className={`px-2 py-1 text-xs rounded-full ${
                                 reg.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                 reg.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                 'bg-red-100 text-red-800'
                               }`}>
                                 {reg.payment_status}
                               </span>
                             </td>
                             <td className="px-4 py-2">
                               <div className="flex gap-2">
                                 <button
                                   onClick={() => handleTransferRegistration(reg)}
                                   className="text-blue-400 hover:text-blue-300"
                                   title="Transfer Registration"
                                 >
                                   <User className="h-4 w-4" />
                                 </button>
                                 <button
                                   onClick={() => handleRemoveRegistration(reg.id)}
                                   className="text-red-400 hover:text-red-300"
                                   title="Remove Registration"
                                 >
                                   <Trash className="h-4 w-4" />
                                 </button>
                               </div>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                 </div>
               )}

               {activeUserTab === 'driver-profiles' && (
                 <div className="space-y-6">
                   <h3 className="text-xl font-semibold text-white mb-4">Driver Profiles</h3>
                   
                   {selectedUserDriverProfiles.map((profile) => (
                     <div key={profile.id} className="bg-zinc-800 rounded-lg p-4 mb-4">
                       <div className="flex justify-between items-center mb-4">
                         <h4 className="text-lg font-medium text-white">
                           {profile.vehicle_type === 'auto' ? 'Auto' : 'Motorcycle'} Profile
                         </h4>
                         <span className={`px-2 py-1 text-xs rounded-full ${
                           profile.is_complete ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                         }`}>
                           {profile.is_complete ? 'Complete' : 'Incomplete'}
                         </span>
                       </div>
                       
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                         <div>
                           <h5 className="text-sm font-medium text-gray-300 mb-2">Personal Info</h5>
                           <div className="space-y-1 text-xs text-gray-400">
                             <div>Name: {profile.personal_info?.fullName || 'N/A'}</div>
                             <div>Phone: {profile.personal_info?.mobilePhone || 'N/A'}</div>
                             <div>ID: {profile.personal_info?.idNumber || 'N/A'}</div>
                           </div>
                         </div>
                         <div>
                           <h5 className="text-sm font-medium text-gray-300 mb-2">Vehicle Info</h5>
                           <div className="space-y-1 text-xs text-gray-400">
                             <div>Race #: {profile.vehicle_info?.raceNumber || 'N/A'}</div>
                             <div>Model: {profile.vehicle_info?.vehicleBrandModel || 'N/A'}</div>
                             <div>Color: {profile.vehicle_info?.color || 'N/A'}</div>
                           </div>
                         </div>
                         <div>
                           <h5 className="text-sm font-medium text-gray-300 mb-2">Driver Info</h5>
                           <div className="space-y-1 text-xs text-gray-400">
                             <div>Driver: {profile.driver_info?.driverName || 'N/A'}</div>
                             <div>License: {profile.driver_info?.licenseNumber || 'N/A'}</div>
                             <div>Team: {profile.driver_info?.teamName || 'N/A'}</div>
                           </div>
                         </div>
                       </div>
                       
                       <button
                         onClick={() => {
                           setEditingProfileId(profile.id);
                           setDriverProfileForm({
                             vehicle_type: profile.vehicle_type,
                             personal_info: profile.personal_info || driverProfileForm.personal_info,
                             vehicle_info: profile.vehicle_info || driverProfileForm.vehicle_info,
                             driver_info: profile.driver_info || driverProfileForm.driver_info
                           });
                           setShowDriverProfileModal(true);
                         }}
                         className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                       >
                         Edit Profile
                       </button>
                     </div>
                   ))}
                 </div>
               )}
            </div>
          </div>
        )}

        {/* Worker Modal */}
        {showWorkerModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Add Worker</h2>
                <button onClick={() => setShowWorkerModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleCreateWorker} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={workerForm.full_name}
                    onChange={(e) => setWorkerForm({ ...workerForm, full_name: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={workerForm.email}
                    onChange={(e) => setWorkerForm({ ...workerForm, email: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={workerForm.phone}
                    onChange={(e) => setWorkerForm({ ...workerForm, phone: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                  <input
                    type="password"
                    value={workerForm.password}
                    onChange={(e) => setWorkerForm({ ...workerForm, password: e.target.value })}
                    required
                    minLength={6}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  Create Worker
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Driver Profile Modal */}
        {showDriverProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-white">
                  Edit Driver Profile - {driverProfileForm.vehicle_type === 'auto' ? 'Auto' : 'Motorcycle'}
                </h2>
                <button onClick={() => setShowDriverProfileModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                if (editingProfileId) {
                  await handleDriverProfileUpdate(editingProfileId);
                }
              }} className="space-y-6">
                
                {/* Personal Information */}
                <div className="bg-zinc-800 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                      <input
                        type="text"
                        value={driverProfileForm.personal_info.fullName}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, fullName: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Address</label>
                      <input
                        type="text"
                        value={driverProfileForm.personal_info.address}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, address: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">ID Number</label>
                      <input
                        type="text"
                        value={driverProfileForm.personal_info.idNumber}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, idNumber: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Date of Birth</label>
                      <input
                        type="date"
                        value={driverProfileForm.personal_info.dateOfBirth}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, dateOfBirth: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Mobile Phone</label>
                      <input
                        type="tel"
                        value={driverProfileForm.personal_info.mobilePhone}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, mobilePhone: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">WhatsApp</label>
                      <input
                        type="tel"
                        value={driverProfileForm.personal_info.whatsapp}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          personal_info: { ...driverProfileForm.personal_info, whatsapp: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="bg-zinc-800 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Vehicle Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Race Number</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.raceNumber}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, raceNumber: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Vehicle Brand/Model</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.vehicleBrandModel}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, vehicleBrandModel: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Color</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.color}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, color: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Engine Brand</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.engineBrand}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, engineBrand: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Year</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.year}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, year: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Number of Cylinders</label>
                      <input
                        type="text"
                        value={driverProfileForm.vehicle_info.numberOfCylinders}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          vehicle_info: { ...driverProfileForm.vehicle_info, numberOfCylinders: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Driver Information */}
                <div className="bg-zinc-800 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Driver Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Driver Name</label>
                      <input
                        type="text"
                        value={driverProfileForm.driver_info.driverName}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, driverName: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">License Number</label>
                      <input
                        type="text"
                        value={driverProfileForm.driver_info.licenseNumber}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, licenseNumber: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Expiration Date</label>
                      <input
                        type="date"
                        value={driverProfileForm.driver_info.expirationDate}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, expirationDate: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Team Name</label>
                      <input
                        type="text"
                        value={driverProfileForm.driver_info.teamName}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, teamName: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Club Name</label>
                      <input
                        type="text"
                        value={driverProfileForm.driver_info.clubName}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, clubName: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Nickname</label>
                      <input
                        type="text"
                        value={driverProfileForm.driver_info.nickname}
                        onChange={(e) => setDriverProfileForm({
                          ...driverProfileForm,
                          driver_info: { ...driverProfileForm.driver_info, nickname: e.target.value }
                        })}
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-md p-2 text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDriverProfileModal(false)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                  >
                    Update Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transfer Registration Modal */}
        {showTransferModal && transferRegistration && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Transfer Registration</h2>
                <button onClick={() => setShowTransferModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              
              <div className="mb-4">
                <div className="bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white mb-4">
                  <div className="font-medium">{transferRegistration.event.name}</div>
                  <div className="text-sm text-gray-400">{transferRegistration.category.name}</div>
                  <div className="text-sm text-gray-400">Current User: {selectedUser?.full_name}</div>
                </div>
              </div>

              <form onSubmit={handleTransferSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Transfer to User</label>
                  <select
                    value={transferTargetUser}
                    onChange={(e) => setTransferTargetUser(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    required
                  >
                    <option value="">Select User</option>
                    {users
                      .filter(u => u.id !== selectedUser?.id && u.role !== 'worker')
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="bg-amber-900/30 border border-amber-800/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-300 text-sm font-medium">Transfer Registration</p>
                      <p className="text-amber-200 text-sm mt-1">
                        The target user must have a complete driver profile for the vehicle type required by this category.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowTransferModal(false)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                    disabled={!transferTargetUser}
                  >
                    Transfer Registration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Temporary Password Modal */}
        {showTempPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Set Temporary Password</h3>
                <button
                  onClick={() => setShowTempPasswordModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSetTemporaryPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    User: {tempPasswordForm.userName}
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Temporary Password
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tempPasswordForm.temporaryPassword}
                      onChange={(e) => setTempPasswordForm({ ...tempPasswordForm, temporaryPassword: e.target.value })}
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      placeholder="Enter temporary password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setTempPasswordForm({ ...tempPasswordForm, temporaryPassword: generateTemporaryPassword() })}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-md"
                      title="Generate New Password"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Expires In (Days)
                  </label>
                  <select
                    value={tempPasswordForm.expiresInDays}
                    onChange={(e) => setTempPasswordForm({ ...tempPasswordForm, expiresInDays: parseInt(e.target.value) })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value={1}>1 Day</option>
                    <option value={3}>3 Days</option>
                    <option value={7}>7 Days</option>
                    <option value={14}>14 Days</option>
                    <option value={30}>30 Days</option>
                  </select>
                </div>

                <div className="bg-yellow-900 bg-opacity-50 border border-yellow-700 rounded-md p-3">
                  <p className="text-yellow-200 text-sm">
                    <strong>Note:</strong> The user will be able to log in with this temporary password and will be required to change it on their first login.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowTempPasswordModal(false)}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-md font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-md font-semibold"
                  >
                    Set Temporary Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}